<script setup lang="ts">
import { LISTING_BASE_PATH, type ValidType } from "#shared/utils/listingTypes";

const { t } = useI18n({ useScope: "local" });

const props = defineProps<{
  activeType: string;
  /** Si défini, les tabs propagent la ville (ex: /cagnottes/lyon → /evenements/lyon) */
  villeSlug?: string | null;
}>();

// Construit l'URL d'un tab en propageant la ville si on en a une.
// Mapping : tab "cagnottes" + ville=lyon → /cagnottes/lyon
//           tab "tout" + ville=lyon       → /recherche?ville=lyon (pas de page /tout/[ville])
//
// Sans ville, on renvoie l'URL CANONIQUE du rayon (/cagnottes, /evenements…)
// et non plus `/recherche/${type}` : ces URLs sont devenues des redirections
// permanentes, et pointer un lien de navigation vers une redirection dilue
// l'autorité à chaque saut (refonte 2026-08-06).
const tabUrl = (tabType: string) => {
  if (props.villeSlug) {
    if (tabType === "tout") return `/recherche?ville=${props.villeSlug}`;
    if (tabType === "associations") return `/associations/ville/${props.villeSlug}`;
    if (tabType === "qr-codes") return "/qr"; // pas de QR par ville
    return `/${tabType}/${props.villeSlug}`;
  }
  return LISTING_BASE_PATH[tabType as ValidType] ?? "/recherche";
};

const tabsRow = ref<HTMLElement | null>(null);

function scrollToActiveTab(smooth = true) {
  nextTick(() => {
    if (!tabsRow.value) return;
    const activeEl = tabsRow.value.querySelector(".border-main") as HTMLElement;
    if (!activeEl) return;
    const scrollLeft = activeEl.offsetLeft - tabsRow.value.offsetWidth / 2 + activeEl.offsetWidth / 2;
    tabsRow.value.scrollTo({ left: Math.max(0, scrollLeft), behavior: smooth ? "smooth" : "instant" });
  });
}

defineExpose({ scrollToActiveTab });

onMounted(() => scrollToActiveTab(false));
</script>

<template>
  <div ref="tabsRow" class="flex items-center gap-0 -mb-px overflow-x-auto scrollbar-hide">
    <!-- Tout -->
    <NuxtLink :to="tabUrl('tout')" class="group px-5 py-3 text-base font-semibold transition-colors relative whitespace-nowrap border-b-2 flex items-center gap-2" :class="activeType === 'tout' ? 'text-main border-main' : 'text-gray-500 hover:text-gray-700 border-transparent'">
      <UIcon name="i-lucide-grid-2x2" class="size-5 transition-transform duration-300 group-hover:scale-110" />
      {{ t('tabs.tout') }}
    </NuxtLink>
    <!-- Associations -->
    <NuxtLink :to="tabUrl('associations')" class="group px-5 py-3 text-base font-semibold transition-colors relative whitespace-nowrap border-b-2 flex items-center gap-2" :class="activeType === 'associations' ? 'text-main border-main' : 'text-gray-500 hover:text-gray-700 border-transparent'">
      <svg class="size-5 shrink-0 transition-transform duration-300 group-hover:scale-110" viewBox="0 0 32 32" fill="none"><circle class="assoc-head1" cx="10" cy="10" r="5" fill="var(--color-ink)"/><path class="assoc-body1" d="M2 28c0-5 3.5-9 8-9s8 4 8 9" fill="var(--color-ink)"/><circle class="assoc-head2" cx="22" cy="10" r="5" fill="url(#donoor-icon-blue)"/><path class="assoc-body2" d="M14 28c0-5 3.5-9 8-9s8 4 8 9" fill="url(#donoor-icon-blue)"/></svg>
      {{ t('tabs.associations') }}
    </NuxtLink>
    <!-- Cagnottes -->
    <NuxtLink :to="tabUrl('cagnottes')" class="group px-5 py-3 text-base font-semibold transition-colors relative whitespace-nowrap border-b-2 flex items-center gap-2" :class="activeType === 'cagnottes' ? 'text-main border-main' : 'text-gray-500 hover:text-gray-700 border-transparent'">
      <svg class="size-5 shrink-0" viewBox="0 0 32 32" fill="none"><ellipse class="cagnotte-body" cx="16" cy="18" rx="12" ry="9" fill="var(--color-ink)"/><ellipse class="cagnotte-snout" cx="26" cy="18" rx="3" ry="2.5" fill="#fff"/><circle class="cagnotte-eye" cx="22" cy="14" r="1.5" fill="var(--color-ink)"/><path class="cagnotte-ear" d="M10 10c-2-3 0-6 3-5s2 4 0 5" fill="var(--color-ink)"/><rect class="cagnotte-slot" x="12" y="9" width="8" height="2" rx="1" fill="var(--color-ink)"/><circle class="cagnotte-coin" cx="16" cy="6" r="3" fill="url(#donoor-icon-blue)"/></svg>
      {{ t('tabs.cagnottes') }}
    </NuxtLink>
    <!-- Evenements -->
    <NuxtLink :to="tabUrl('evenements')" class="group px-5 py-3 text-base font-semibold transition-colors relative whitespace-nowrap border-b-2 flex items-center gap-2" :class="activeType === 'evenements' ? 'text-main border-main' : 'text-gray-500 hover:text-gray-700 border-transparent'">
      <svg class="size-5 shrink-0" viewBox="0 0 32 32" fill="none"><rect class="event-cal" x="4" y="6" width="24" height="22" rx="4" fill="var(--color-ink)"/><rect x="4" y="6" width="24" height="8" rx="4" fill="var(--color-ink)"/><rect class="event-ring1" x="10" y="3" width="3" height="6" rx="1.5" fill="var(--color-ink)"/><rect class="event-ring2" x="19" y="3" width="3" height="6" rx="1.5" fill="var(--color-ink)"/><rect class="event-day1" x="8" y="17" width="5" height="4" rx="1" fill="#fff"/><rect class="event-day2" x="15" y="17" width="5" height="4" rx="1" fill="url(#donoor-icon-blue)"/><rect class="event-day3" x="22" y="17" width="4" height="4" rx="1" fill="#fff"/><circle class="event-dot" cx="17.5" cy="19" r="1.5" fill="url(#donoor-icon-blue)"/></svg>
      {{ t('tabs.evenements') }}
    </NuxtLink>
    <!-- Adhesions -->
    <NuxtLink :to="tabUrl('adhesions')" class="group px-5 py-3 text-base font-semibold transition-colors relative whitespace-nowrap border-b-2 flex items-center gap-2" :class="activeType === 'adhesions' ? 'text-main border-main' : 'text-gray-500 hover:text-gray-700 border-transparent'">
      <svg class="size-5 shrink-0" viewBox="0 0 32 32" fill="none"><rect class="member-card" x="3" y="7" width="26" height="18" rx="4" fill="var(--color-ink)"/><rect x="3" y="7" width="26" height="7" rx="4" fill="url(#donoor-icon-blue)"/><circle class="member-avatar" cx="10" cy="20" r="3" fill="var(--color-ink)"/><rect class="member-line1" x="16" y="18" width="10" height="2" rx="1" fill="#fff"/><rect class="member-line2" x="16" y="22" width="7" height="2" rx="1" fill="#fff"/></svg>
      {{ t('tabs.adhesions') }}
    </NuxtLink>
    <!-- Boutiques -->
    <NuxtLink :to="tabUrl('boutiques')" class="group px-5 py-3 text-base font-semibold transition-colors relative whitespace-nowrap border-b-2 flex items-center gap-2" :class="activeType === 'boutiques' ? 'text-main border-main' : 'text-gray-500 hover:text-gray-700 border-transparent'">
      <svg class="size-5 shrink-0" viewBox="0 0 32 32" fill="none"><path class="shop-awning" d="M4 6h24l2 6H2l2-6z" fill="var(--color-ink)"/><rect class="shop-body" x="5" y="12" width="22" height="16" rx="2" fill="var(--color-ink)"/><rect class="shop-door" x="12" y="18" width="8" height="10" rx="1" fill="var(--color-ink)"/><circle class="shop-handle" cx="18" cy="23" r="1" fill="url(#donoor-icon-blue)"/><rect class="shop-window" x="7" y="15" width="4" height="4" rx="1" fill="#fff"/><rect class="shop-window2" x="21" y="15" width="4" height="4" rx="1" fill="url(#donoor-icon-blue)"/></svg>
      {{ t('tabs.boutiques') }}
    </NuxtLink>
    <!-- QR Codes — onglet MASQUÉ (Karim 2026-08-22) : peu utile pour
         l'instant. La page /qr reste servie et le type `qr-codes` reste
         valide : pour le rétablir, décommenter ce bloc.
    <NuxtLink :to="tabUrl('qr-codes')" class="group px-5 py-3 text-base font-semibold transition-colors relative whitespace-nowrap border-b-2 flex items-center gap-2" :class="activeType === 'qr-codes' ? 'text-main border-main' : 'text-gray-500 hover:text-gray-700 border-transparent'">
    <svg class="size-5 shrink-0" viewBox="0 0 32 32" fill="none"><rect class="qr-tl" x="4" y="4" width="10" height="10" rx="2" fill="var(--color-ink)"/><rect x="6" y="6" width="6" height="6" rx="1" fill="var(--color-ink)"/><rect x="7.5" y="7.5" width="3" height="3" fill="var(--color-ink)"/><rect class="qr-tr" x="18" y="4" width="10" height="10" rx="2" fill="var(--color-ink)"/><rect x="20" y="6" width="6" height="6" rx="1" fill="var(--color-ink)"/><rect x="21.5" y="7.5" width="3" height="3" fill="var(--color-ink)"/><rect class="qr-bl" x="4" y="18" width="10" height="10" rx="2" fill="var(--color-ink)"/><rect x="6" y="20" width="6" height="6" rx="1" fill="var(--color-ink)"/><rect x="7.5" y="21.5" width="3" height="3" fill="var(--color-ink)"/><rect x="18" y="18" width="4" height="4" rx="1" fill="url(#donoor-icon-blue)"/><rect x="24" y="18" width="4" height="4" rx="1" fill="url(#donoor-icon-blue)"/><rect x="18" y="24" width="4" height="4" rx="1" fill="url(#donoor-icon-blue)"/><rect x="24" y="24" width="4" height="4" rx="1" fill="var(--color-ink)"/></svg>
    QR Codes
    </NuxtLink>
    -->
    <!-- Slot trailing : permet au parent d'injecter le bouton Filtres
         poussé à droite via ml-auto (Karim 2026-06-25). Comme ça il fait
         partie du flex natif du tabsbar, parfaitement aligné, pas
         d'overlap avec le contenu en dessous. -->
    <div class="ml-auto pl-3 pr-1 shrink-0 self-center -mb-px">
      <slot name="trailing" />
    </div>
  </div>
</template>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Tab icon animations on hover */
.cagnotte-body, .cagnotte-snout, .cagnotte-eye, .cagnotte-ear, .cagnotte-slot, .cagnotte-coin {
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: center;
}
.cagnotte-ear { transform-origin: 12px 10px; }
.group:hover .cagnotte-body { transform: scale(1.05) translateY(1px); }
.group:hover .cagnotte-snout { transform: translateX(2px); }
.group:hover .cagnotte-eye { transform: scale(1.3); }
.group:hover .cagnotte-ear { transform: rotate(-15deg); }
.group:hover .cagnotte-slot { transform: scaleX(1.1); }
.group:hover .cagnotte-coin { transform: translateY(4px) scale(0.9); }

.event-cal, .event-ring1, .event-ring2, .event-day1, .event-day2, .event-day3, .event-dot {
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: center;
}
.event-ring1, .event-ring2 { transform-origin: center bottom; }
.group:hover .event-cal { transform: scale(1.04); }
.group:hover .event-ring1 { transform: translateY(-2px); }
.group:hover .event-ring2 { transform: translateY(-2px); }
.group:hover .event-day2 { transform: scale(1.15); }
.group:hover .event-dot { transform: scale(1.4); }

.member-card, .member-avatar, .member-line1, .member-line2 {
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: center;
}
.member-line1, .member-line2 { transform-origin: left center; }
.group:hover .member-card { transform: scale(1.08) rotate(-2deg); }
.group:hover .member-avatar { transform: scale(1.3) translateY(-2px); }
.group:hover .member-line1 { transform: scaleX(1.2) translateX(2px); }
.group:hover .member-line2 { transform: scaleX(1.15) translateX(2px); }

.qr-tl, .qr-tr, .qr-bl {
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: center;
}
.group:hover .qr-tl { transform: translate(-2px, -2px); }
.group:hover .qr-tr { transform: translate(2px, -2px); }
.group:hover .qr-bl { transform: translate(-2px, 2px); }

.assoc-head1, .assoc-body1, .assoc-head2, .assoc-body2 {
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: center;
}
.group:hover .assoc-head1, .group:hover .assoc-body1 { transform: translateX(-2px); }
.group:hover .assoc-head2, .group:hover .assoc-body2 { transform: translateX(2px); }
</style>

<i18n lang="json" src="../../../i18n/pages/components/recherche-page/RechercheTabsBar.json"></i18n>
